"use client";

import { useState } from "react";
import Link from "next/link";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/use-fetch";
import { Button, Modal } from "@/components/ui";
import { useToast } from "@/components/toast";
import type { Contact, ContactGroup, MessageTemplate } from "@/lib/types";

// ---------------------------------------------------------------------------
// Load a saved template into whatever message box the caller owns.
// ---------------------------------------------------------------------------
export function TemplatePicker({ onPick }: { onPick: (body: string) => void }) {
  const [open, setOpen] = useState(false);
  // Shared cache: templates load once (warmed on sign-in) and the modal opens instantly thereafter.
  const { data, error } = useFetch<{ templates: MessageTemplate[] }>("account/templates", { staleMs: 60000 });
  const rows = error ? [] : data?.templates ?? null;

  return (
    <>
      <Button size="sm" variant="secondary" type="button" onClick={() => setOpen(true)}>Use template</Button>

      <Modal open={open} onClose={() => setOpen(false)} title="Load a template" widthClass="max-w-lg">
        {rows === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            You have no saved templates yet.{" "}
            <Link href="/templates" className="font-medium text-brand-600 hover:underline">Create one →</Link>
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((t) => (
              <li key={t.templateId}>
                <button
                  type="button"
                  onClick={() => { onPick(t.body); setOpen(false); }}
                  className="w-full rounded-lg px-2 py-2.5 text-left transition hover:bg-gray-50"
                >
                  <p className="font-medium text-gray-900">{t.name}</p>
                  <p className="truncate text-sm text-gray-500">{t.body}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Load a saved contact group. The caller receives the group's contacts (number +
// name + merge attributes), so a bulk page can take just the numbers while a
// personalized page can also use the variables.
// ---------------------------------------------------------------------------
export function GroupPicker({
  onPick,
  label = "Load from group"
}: {
  onPick: (contacts: Contact[], group: ContactGroup) => void;
  label?: string;
}) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  // Shared cache: the group list loads once (warmed on sign-in) so the picker opens instantly.
  const { data, error } = useFetch<{ groups: ContactGroup[] }>("account/contact-groups", { staleMs: 60000 });
  const groups = error ? [] : data?.groups ?? null;

  async function choose(g: ContactGroup) {
    setBusy(g.groupId);
    try {
      const res = await api<{ recipients: Contact[] }>(`account/contact-groups/recipients?groupId=${g.groupId}`);
      const list = res.recipients || [];
      if (list.length === 0) {
        toast.error(`"${g.name}" has no contacts yet.`);
        return;
      }
      onPick(list, g);
      setOpen(false);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Button size="sm" variant="secondary" type="button" onClick={() => setOpen(true)}>{label}</Button>

      <Modal open={open} onClose={() => setOpen(false)} title="Load a contact group" widthClass="max-w-lg">
        {groups === null ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="text-sm text-gray-500">
            You have no contact groups yet.{" "}
            <Link href="/contacts" className="font-medium text-brand-600 hover:underline">Create one →</Link>
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {groups.map((g) => (
              <li key={g.groupId}>
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => choose(g)}
                  className="flex w-full items-center justify-between rounded-lg px-2 py-2.5 text-left transition hover:bg-gray-50 disabled:opacity-50"
                >
                  <span>
                    <span className="block font-medium text-gray-900">{g.name}</span>
                    {g.description && <span className="block text-xs text-gray-500">{g.description}</span>}
                  </span>
                  <span className="text-xs text-gray-500">
                    {busy === g.groupId ? "Loading…" : `${g.memberCount.toLocaleString()} contact(s)`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </>
  );
}
